<template>
  <div class="min-h-screen text-slate-100 overflow-x-hidden relative">
    <!-- Animated Background Layer -->
    <div class="fixed inset-0 z-0 bg-[#060811]">
      <!-- Background Images with Crossfade -->
      <div class="absolute inset-0 bg-images-container">
        <div class="bg-image bg-image-1" :style="{ backgroundImage: `url('${bgImage1}')` }"></div>
        <div class="bg-image bg-image-2" :style="{ backgroundImage: `url('${bgImage2}')` }"></div>
        <div class="bg-image bg-image-3" :style="{ backgroundImage: `url('${bgImage3}')` }"></div>
      </div>
      <!-- Gradient Overlay -->
      <div class="absolute inset-0 bg-gradient-to-b from-[#060811]/30 via-[#060811]/15 to-[#060811]/35"></div>
    </div>

    <!-- Content -->
    <div class="relative z-10">
      <AppHeader
        :account="account"
        :eth="ethBalDisplay"
        :usdt="usdtBalDisplay"
        :isLoadingBalances="isLoadingBalances"
        :devMode="DEV_MODE"
        @connect="connect"
      />

    <!-- User Activity Toast Notifications -->
    <div class="fixed bottom-4 right-4 md:bottom-auto md:top-24 z-50 space-y-2 pointer-events-none">
      <TransitionGroup name="toast">
        <div
          v-for="toast in activeToasts"
          :key="toast.id"
          class="toast-notification"
        >
          <div class="toast-glow"></div>
          <div class="toast-content">
            <div class="flex items-center space-x-2">
              <div class="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              <span class="text-xs text-gray-400">User</span>
              <span class="text-xs font-mono text-cyan-400">{{ toast.address }}</span>
              <span class="text-xs text-gray-400">is online</span>
            </div>
          </div>
        </div>
      </TransitionGroup>
    </div>

    <!-- WELCOME MODAL (Terms & Conditions) -->
    <WelcomeModal />

    <!-- GLOBAL TX OVERLAY -->
    <TxOverlay
      :txOpen="txOpen"
      :txMode="txMode"
      :txTitle="txTitle"
      :txDetail="txDetail"
      :iconSuccess="iconSuccess"
      :iconError="iconError"
      @close="hideTx"
    />

    <!-- BUY PAGE -->
    <div v-if="$route.path === '/buy'" class="max-w-6xl xl:max-w-7xl 2xl:max-w-[90rem] mx-auto px-4 sm:px-6 py-6 space-y-6 mt-20 sm:mt-24 md:mt-26 pb-24 lg:pb-20">
      <section class="grid gap-4 sm:gap-6 md:grid-cols-3 items-stretch min-w-0">
        <!-- LEFT (1/3) -->
        <div class="space-y-3">
          <BuyLeft
            v-model:buyVrxInput="buyVrxInput"
            v-model:buyLockIndex="buyLockIndex"
            :previewCostVRXDisplay="previewCostVRXDisplay"
            :VRX_ADDR="VRX_ADDR"
            :shortAddr="shortAddr"
            :ensureUSDTAllowanceAndBuyBundle="ensureUSDTAllowanceAndBuyBundle"
            :hasActiveStake="hasActiveStake"
            :currentLockIndex="lockIdx"
            :maxBalance="maxVRXCanBuy"
            :bonusAvailable="bonusAvailable"
            :priceVRX="priceVRX"
            :remainingVRXForSale="remainingVRXForSale"
          />
        </div>


        <!-- RIGHT (2/3) -->
        <BuyRight
          :priceVRXDisplay2dp="priceVRXDisplay2dp"
          :priceKEYDisplay2dp="priceKEYDisplay2dp"
          :keysSoldLabel="keysSoldLabel"
          :percentSoldStyle="percentSoldStyle"
          :soldRemainingLabel="soldRemainingLabel"
          :buySingleKey="buySingleKey"
          :holdersLoading="holdersLoading"
          :holdersError="holdersError"
          :topHolders="topHolders"
          :formatVRX="formatVRX"
          :totalVRXHoldersDisplay="totalVRXHoldersDisplay"
          :shortAddr="shortAddr"
        />
      </section>

      <!-- FULL-WIDTH: My Holdings -->
      <MyHoldings
        :vrxBal="vrxBal"
        :keyBal="keyBal"
        :stakedVRX="stakedVRX"
        :stakedKeys="stakedKeys"
        :fmt18="fmt18"
        :fmt0="fmt0"
      />

      <!-- FULL-WIDTH: Claim Vested -->
      <ClaimVested
        :account="account"
        :vestedTotal="vestedTotal"
        :vestedClaimed="vestedClaimed"
        :vestedClaimable="vestedClaimable"
        :vestedStart="vestedStart"
        :vestedDuration="vestedDuration"
        :vestedCliff="vestedCliff"
        :fmt18="fmt18"
        :claimVested="testClaimVested"
      />
    </div>

    <!-- STAKE PAGE -->
    <div v-else-if="$route.path === '/stake'" class="max-w-6xl xl:max-w-7xl 2xl:max-w-[90rem] mx-auto px-4 sm:px-6 py-6 mt-20 sm:mt-24 md:mt-26 pb-24 lg:pb-20 min-w-0 overflow-hidden">
      <!-- Header Row: Payout Pool + Claim Panel -->
      <div class="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 mb-4 md:h-40 min-w-0">
        <StakeHeader
          :globalPayoutAmountDisplay="globalPayoutAmountDisplay"
          :daysUntilPayout="daysUntilPayout"
          :payoutDateLabel="payoutDateLabel"
          :payoutProgressStyle="payoutProgressStyle"
          :salesChartData="salesChartData"
          :isLoading="isFetchingSales"
        />
        
        <div class="w-full md:w-64 min-w-0">
          <ClaimPanel
            :pendingUSDTDisplay="pendingUSDTDisplay"
            :canClaim="canClaim"
            :claim="claim"
          />
        </div>
      </div>

      <!-- Main Grid: Stake Form + Stats/Positions -->
      <div class="grid md:grid-cols-3 gap-4 min-w-0">
        <!-- Left Column: Stake Form -->
        <div class="md:col-span-1 min-w-0">
          <StakeForm
            v-model:stakeLockIndex="stakeLockIndex"
            v-model:stakeVrxInput="stakeVrxInput"
            v-model:stakeKeyInput="stakeKeyInput"
            :hasActiveStake="hasActiveStake"
            :keysMultiplier="keysMultiplier"
            :ensureAllowancesAndStake="ensureAllowancesAndStake"
            :vrxBal="vrxBal"
            :keyBal="keyBal"
            :fmt18="fmt18"
            :fmt0="fmt0"
            :currentLockIndex="lockIdx"
            :stakedVRX="stakedVRX"
            :stakedKeys="stakedKeys"
          />
        </div>

        <!-- Right Column: Stats Chart + My Positions -->
        <div class="md:col-span-2 flex flex-col gap-4 min-w-0">
          <StakeStatsChart
            :totalStakedDisplay="totalStakedDisplay"
            :mySharesDisplay="mySharesDisplay"
            :ownershipPctDisplay="ownershipPctDisplay"
            :chartPathArea="chartPathArea"
            :chartPathLine="chartPathLine"
            :chartPointsPx="chartPointsPx"
            :chartSummary="chartSummary"
          />
          
          <MyPositions
            :hasStake="hasStake"
            :canUnstake="canUnstake"
            :unstakeAll="unstakeAll"
            :stakedVRX="stakedVRX"
            :lockLenDays="lockLenDays"
            :lockMult="lockMult"
            :lockStartTs="lockStartTs"
            :lockEndTs="lockEndTs"
            :lockProgressPct="lockProgressPct"
            :keysMultiplier="keysMultiplier"
            :mySharesRowDisplay="mySharesRowDisplay"
            :lockHuman="lockHuman"
            :fmtDMY="fmtDMY"
          />
        </div>
      </div>
    </div>

    <!-- DEV PAGE -->
    <div v-else-if="$route.path === '/dev' && DEV_MODE" class="max-w-6xl xl:max-w-7xl 2xl:max-w-[90rem] mx-auto px-4 sm:px-6 py-6 mt-20 sm:mt-24 md:mt-26 pb-24 lg:pb-20 min-w-0">
      <div class="mb-6">
        <h2 
          class="bg-gradient-to-r from-[#B8DAFF] to-[#1A6ECA] bg-clip-text text-transparent text-2xl md:text-3xl font-bold"
          style="font-family: Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial"
        >
          Developer Tools
        </h2>
        <p class="text-white/50 text-sm mt-2"
           style="font-family: Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial">
          Admin functions for testing and managing the VRX ecosystem
        </p>
      </div>

      <DevPanel
        :account="account"
        v-model:payoutInput="payoutInput"
        @testPayout="testPayout"
        :vestedClaimable="vestedClaimable"
        :vestedClaimableDisplay="vestedClaimableDisplay"
        @refreshVestedInfo="refreshVestedInfo"
        @testClaimVested="testClaimVested"
        v-model:vestTo="vestTo"
        v-model:vestAmountInput="vestAmountInput"
        v-model:vestLockDays="vestLockDays"
        v-model:vestCliffDays="vestCliffDays"
        @testSendVested="testSendVested"
        v-model:mintPoolAmountInput="mintPoolAmountInput"
        :vestingPoolAvailableDisplay="vestingPoolAvailableDisplay"
        @refreshVestingPool="refreshVestingPool"
        @testMintToVestingPool="testMintToVestingPool"
      />
    </div>
      <AppFooter :vrx="VRX_ADDR" :usdt="USDT_ADDR" :key-addr="KEY_ADDR" :core="CORE_ADDR" />
    </div>
  </div>
</template>

<script setup>
  import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
  import { ethers } from 'ethers'

  import AppHeader from './components/AppHeader.vue'
  import AppFooter from './components/AppFooter.vue'

  /* New sub-components */
  import WelcomeModal from './components/WelcomeModal.vue'
  import TxOverlay from './components/TxOverlay.vue'
  import BuyLeft from './components/BuyLeft.vue'
  import BuyRight from './components/BuyRight.vue'
  import MyHoldings from './components/MyHoldings.vue'
  import StakeHeader from './components/StakeHeader.vue'
  import StakeForm from './components/StakeForm.vue'
  import StakeStatsChart from './components/StakeStatsChart.vue'
  import MyPositions from './components/MyPositions.vue'
  import ClaimPanel from './components/ClaimPanel.vue'
  import ClaimVested from './components/ClaimVested.vue'
  import DevPanel from './components/DevPanel.vue'

  /* Assets used only by overlay here */
  import iconSuccess from './assets/GreenCheck.png'
  import iconError   from './assets/ErrorImage.png'
  import iconVRX from './assets/VRX.png'

  /* Background images - served from public folder */
  const bgImage1 = '/backgrounds/Starvara-Youtube-Cover-2560x1440-x4.jpg'
  const bgImage2 = '/backgrounds/Starvara-Youtube-Cover-2560x1440-x2.jpg'
  const bgImage3 = '/backgrounds/Starvara-Youtube-Cover-2560x1440-x3.jpg'

  /* ---------------- CONFIG ---------------- */
  const DEV_MODE = ref(false) // Set to false to hide dev tab and admin tools

  const INFURA_ID = "94fce5cb8c234043a985344e82f0002d"
  const RPC = `https://mainnet.infura.io/v3/${INFURA_ID}`

  const CORE_ADDR = ref('0x0fddf3E36650DCfD8132da30C13562565c16Cc05')
  const USDT_ADDR = ref('0xdAC17F958D2ee523a2206206994597C13D831ec7')
  const VRX_ADDR  = ref('0xA1336140e4a8f15eA3Be43C06824Eb1eF2d04c03')
  const KEY_ADDR  = ref('0xBeF682fCc1030Ff62A4aBdd7AC5cd2B7Ad914849')

  /* ---------------- ABIs (v3) ---------------- */
  const ERC20_ABI = [
    "function decimals() view returns (uint8)",
    "function balanceOf(address) view returns (uint256)",
    "function allowance(address owner, address spender) view returns (uint256)",
    "function approve(address spender, uint256 value) returns (bool)"
  ]

  const CORE_ABI = [
    "function usdt() view returns (address)",
    "function vrx() view returns (address)",
    "function key() view returns (address)",
    "function priceUSDTPerVRX() view returns (uint256)",
    "function priceUSDTPerKEY() view returns (uint256)",
    "function remainingVRXForSale() view returns (uint256)",
    "function remainingKEYForSale() view returns (uint256)",
    "function previewVRXCost(uint256) view returns (uint256)",
    "function previewKEYCost(uint256) view returns (uint256)",
    "function userInfo(address) view returns (uint256 vrxStaked, uint256 keysStaked, uint256 rewardDebt, uint64 lockEnd, uint8 lockIndex)",
    "function pendingRewards(address) view returns (uint256)",
    "function secondsUntilUnlock(address) view returns (uint256)",
    "function lockDetails(address) view returns (uint64 lockStart, uint64 lockEnd, uint8 lockIndex, uint256 secondsLeft)",
    "function buyVRX(uint256)",
    "function buyVRXAndStake(uint256,uint8)",
    "function buyKEY(uint256)",
    "function buyKEYAndStake(uint256,uint8)",
    "function buyBundle(uint256,uint256)",
    "function buyAndStakeBundle(uint256,uint256,uint8)",
    "function stake(uint256,uint256,uint8)",
    "function unstakeAll()",
    "function claimRewards()",
    "function payout(uint256)",
    "function getSnapshots() view returns (uint256[30], uint256[30], uint256[30], uint256[30])",
    "function priceUSDTPerVRX6() view returns (uint256)",
    "function sendVested(address to,uint256 amount,uint256 lockSeconds,uint256 cliffSeconds)",
    "function userStakedTotal() view returns (uint256)",
    "function previewVRXForBuyer(address,uint256) view returns (uint256 vrxToReceive, uint256 costBaseUnits)",
    "function hasBoughtVRX(address) view returns (bool)"
  ]

  /* ---------------- VRX admin ABI (vesting) ---------------- */
  const VRX_ADMIN_ABI = [
    "function owner() view returns (address)",
    "function availableVestingPool() view returns (uint256)",
    "function sendVested(address to,uint256 amount,uint256 daysDuration,uint256 cliffDays)",
    "function mintToVestingPool(uint256 amount)",
    "function vestedInfo(address user) view returns (uint256 total,uint256 claimed,uint256 claimable,uint64 start,uint64 duration,uint64 cliff)",
    "function claimVested()"
  ]

  /* ---------------- Etherscan (holders) ---------------- */
  const ETHERSCAN_API_KEY = 'XW6F49CX5DGDYYE9P4BIMZJR9QCB4ZGC96'
  const ETHERSCAN_API     = 'https://api.etherscan.io/v2/api'
  const ETHERSCAN_CHAINID = '1'

  /* ---------------- Moralis (holders) ---------------- */
  const MORALIS_API_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJub25jZSI6ImM3MzQ0MTJiLTFkYjktNDBiNi1iYjYxLWY3YWQ2ZjQ4NTk2YyIsIm9yZ0lkIjoiNDc4MTM0IiwidXNlcklkIjoiNDkxOTAwIiwidHlwZUlkIjoiMWM0ZWFjYWQtYTc3Ni00ZWY2LWExM2UtNzlmNTdmZmQ5ZTk1IiwidHlwZSI6IlBST0pFQ1QiLCJpYXQiOjE3NjE1NTgzNTMsImV4cCI6NDkxNzMxODM1M30.jZWTtkWoSjd0zr9rfGQvi8QvDEGjxEvqQuskx2MSByg'
  const MORALIS_BASE    = 'https://deep-index.moralis.io/api/v2.2'
  const MORALIS_CHAIN   = 'eth'

  /* ---------------- Providers (read-only) ---------------- */
  const readProvider = new ethers.JsonRpcProvider(RPC)
  const coreRead = ref(null)
  const usdtRead = ref(null)
  const vrxRead  = ref(null)
  const keyRead  = ref(null)
  let vrxAdminRead = null

  /* ---------------- State ---------------- */
  const TOP_N = 5
  const totalStakedVRXChain = ref(0n)
  const account = ref('')
  const chainName = 'Ethereum Mainnet (Infura)'
  const usdtDecimals = ref(6)

  const priceVRX = ref(0n)
  const priceKEY = ref(0n)
  const remainingVRXForSale = ref(0n)
  const remainingKEYForSale = ref(0n)

  const usdtBal = ref(0n)
  const vrxBal  = ref(0n)
  const keyBal  = ref(0n)
  const stakedVRX  = ref(0n)
  const stakedKeys = ref(0n)
  const pending = ref(0n)
  const isLoadingBalances = ref(false)

  const lockSecondsLeft = ref(null)
  const lockIdx = ref(0)

  const buyVrxInput = ref('')
  const buyKeyInput = ref('')

  const stakeVrxInput = ref('')
  const stakeKeyInput = ref('')
  const stakeLockIndex = ref(0)
  const buyLockIndex = ref(0)

  const vrxInfoRead = ref(null)
  const payoutInput = ref('')
  const txStatus = ref('')
  const saleDataReady = ref(false)

  const topHolders = ref([]) // [{ address, balance }]
  const totalVRXHolders = ref(0)
  const holdersLoading = ref(false)
  const holdersError = ref('')

  const bonusAvailable = ref(null)           // null | true | false
  const previewReceiveVRXDisplay = ref('0')  // human-friendly VRX receive preview
  const bonusApplied = ref(false)   

  const VRX_START_BLOCK = undefined
  const INFURA_PACING_MS = 450
  const MAX_RETRIES = 5
  const TRANSFER_TOPIC = ethers.id("Transfer(address,address,uint256)")
  const ERC20_EVENTS_IFACE = new ethers.Interface([
    "event Transfer(address indexed from, address indexed to, uint256 value)"
  ])
  const LOGS_CHUNK = 20_000
  const TOTAL_KEYS_FOR_SALE = 1000

  const remainingKeysCount = computed(() => {
    const rem = Number(remainingKEYForSale.value ?? 0n)
    return Math.max(0, Math.min(TOTAL_KEYS_FOR_SALE, rem))
  })

  const DEFAULT_TEST_PAYOUT = '0.01'
  async function testPayout() {
    try {
      if (!payoutInput.value || Number(payoutInput.value) <= 0) {
        payoutInput.value = DEFAULT_TEST_PAYOUT
      }
      await ensureUsdtAllowanceAndPayout()
    } catch (e) {
      console.error('Test payout failed:', e)
      showError(extractErr(e))
    }
  }

  // --- Dev: sendVested (VRX) ---
  const vestTo = ref('')
  const vestAmountInput = ref('')
  const vestLockDays = ref('')
  const vestCliffDays = ref('')

  async function testSendVested() {
    try {
      await initRead()
      const { vrxAdminW } = await getMM()

      const toRaw   = (vestTo.value || '').trim()
      const amt18   = toUnitsSafe(vestAmountInput.value, 18)
      const daysDur = Number(vestLockDays.value || 0)
      const cliff   = Number(vestCliffDays.value || 0)

      if (!ethers.isAddress(toRaw)) { alert('Invalid recipient address'); return }
      if (amt18 <= 0n) { alert('Amount must be > 0'); return }
      if (!(daysDur > 0)) { alert('Duration (days) must be > 0'); return }
      if (!(cliff < daysDur)) { alert('Cliff must be < duration'); return }

      // Owner check
      const owner = await vrxAdminW.owner()
      if (owner.toLowerCase() !== account.value.toLowerCase()) {
        showError(`sendVested is owner-only. VRX owner: ${shortAddr(owner)}. Connect the owner wallet or transfer ownership.`)
        return
      }

      // Pool check
      const pool = await vrxAdminRead.availableVestingPool()
      if (pool < amt18) {
        const needed = Number(ethers.formatUnits(amt18 - pool, 18)).toLocaleString()
        showError(`Insufficient vesting pool. Top up ~${needed} VRX with mintToVestingPool() first.`)
        return
      }

      showLoading('Sending vested VRX...')
      const tx = await vrxAdminW.sendVested(toRaw, amt18, daysDur, cliff)
      await tx.wait()

      showSuccess('sendVested complete.')
      refreshInBackground()
    } catch (e) {
      console.error('sendVested error:', e)
      // Common reasons surfaced nicely
      const m = extractErr(e)
      if (/caller.*not.*owner/i.test(m)) {
        showError('Only the VRX owner can call sendVested.')
        return
      }
      if (/insufficient pool/i.test(m)) {
        showError('Insufficient vesting pool. Use mintToVestingPool() as owner to fund it.')
        return
      }
      if (/existing grant/i.test(m)) {
        showError('Recipient already has an active grant. They must fully claim it before receiving a new one.')
        return
      }
      showError(m)
    }
  }

  // --- Dev: claimVested (VRX) ---
  const vestedClaimable = ref(0n)
  const vestedTotal = ref(0n)
  const vestedClaimed = ref(0n)
  // optional: start/duration/cliff if you want to display them later
  const vestedStart = ref(0)
  const vestedDuration = ref(0)
  const vestedCliff = ref(0)

  const vestedClaimableDisplay = computed(() => {
    const n = Number(ethers.formatUnits(vestedClaimable.value ?? 0n, 18))
    return n.toLocaleString('en-US', { maximumFractionDigits: 4 })
  })

  async function refreshVestedInfo() {
    try {
      await initRead()
      if (!account.value) return
      // vrxAdminRead was created in initRead()
      const [t, c, claimable, s, d, cl] = await vrxAdminRead.vestedInfo(account.value)
      vestedTotal.value = t
      vestedClaimed.value = c
      vestedClaimable.value = claimable
      vestedStart.value = Number(s)
      vestedDuration.value = Number(d)
      vestedCliff.value = Number(cl)
    } catch (e) {
      // If no grant exists, reset to zeroes
      vestedTotal.value = 0n
      vestedClaimed.value = 0n
      vestedClaimable.value = 0n
    }
  }
  async function refreshFirstBuyFlag() {
    try {
      await initRead()
      if (!account.value) { bonusAvailable.value = null; return }
      const bought = await coreRead.value.hasBoughtVRX(account.value)
      bonusAvailable.value = !bought
    } catch {
      bonusAvailable.value = null
    }
  }

  async function refreshVRXPreview() {
    try {
      await initRead()
      const val = buyVrxInput.value
      if (!val || Number(val) <= 0) {
        previewReceiveVRXDisplay.value = '0'
        bonusApplied.value = false
        return
      }
      const req18 = toUnitsSafe(val, 18)
      const [recv18] = await coreRead.value.previewVRXForBuyer(account.value || ethers.ZeroAddress, req18)
      previewReceiveVRXDisplay.value = Number(ethers.formatUnits(recv18, 18))
        .toLocaleString('en-US', { maximumFractionDigits: 4 })
      bonusApplied.value = recv18 > req18
    } catch {
      // Fallback if the function isn't available (older contract):
      previewReceiveVRXDisplay.value = buyVrxInput.value || '0'
      bonusApplied.value = false
    }
  }

  async function testClaimVested() {
    try {
      await initRead()
      const { vrxAdminW } = await getMM()

      showLoading('Claiming vested VRX...')
      const tx = await vrxAdminW.claimVested()
      await tx.wait()

      showSuccess('Claim vested complete.')
      await refreshVestedInfo()
      refreshInBackground()
    } catch (e) {
      console.error('claimVested error:', e)
      const m = extractErr(e)
      if (/no grant/i.test(m)) {
        showError('No vested grant found for this account.')
        return
      }
      if (/nothing to claim/i.test(m)) {
        showError('Nothing to claim yet (cliff not reached or fully claimed).')
        return
      }
      showError(m)
    }
  }

  // --- Dev: mintToVestingPool (VRX) ---
  const mintPoolAmountInput = ref('')
  const vestingPoolAvailable = ref(0n)

  const vestingPoolAvailableDisplay = computed(() => {
    const n = Number(ethers.formatUnits(vestingPoolAvailable.value ?? 0n, 18))
    return n.toLocaleString('en-US', { maximumFractionDigits: 4 })
  })

  async function refreshVestingPool() {
    try {
      await initRead()
      // vrxAdminRead is created in initRead (VRX_ADDR + VRX_ADMIN_ABI)
      const v = await vrxAdminRead.availableVestingPool()
      vestingPoolAvailable.value = v
    } catch (e) {
      console.warn('refreshVestingPool failed:', e)
    }
  }

  async function testMintToVestingPool() {
    try {
      await initRead()
      const { vrxAdminW } = await getMM()

      const owner = await vrxAdminW.owner()
      if (owner.toLowerCase() !== account.value.toLowerCase()) {
        showError(`mintToVestingPool is owner-only. VRX owner: ${shortAddr(owner)}.`)
        return
      }

      const amt18 = toUnitsSafe(mintPoolAmountInput.value, 18)
      if (amt18 <= 0n) return

      showLoading('Minting to vesting pool...')
      const tx = await vrxAdminW.mintToVestingPool(amt18)
      await tx.wait()

      showSuccess('Pool funded.')
      await refreshVestingPool()
    } catch (e) {
      console.error('mintToVestingPool error:', e)
      showError(extractErr(e))
    }
  }

  /* --- TX Overlay --- */
  const txOpen  = ref(false)
  const txMode  = ref('loading')
  const txTitle = ref('')
  const txDetail = ref('')
  function showLoading(title = 'Working...', detail = '') {
    txTitle.value = title; txDetail.value = detail; txMode.value = 'loading'; txOpen.value = true
  }
  function showSuccess(title = 'Transaction successful', detail = '') {
    txTitle.value = title; txDetail.value = detail; txMode.value = 'success'; txOpen.value = true
  }
  function showError(detail = '') {
    txTitle.value = 'Transaction failed'; txDetail.value = detail; txMode.value = 'error'; txOpen.value = true
  }
  function hideTx() { txOpen.value = false }

  const keysSold = computed(() => Math.max(0, TOTAL_KEYS_FOR_SALE - remainingKeysCount.value))
  const keysSoldLabel = computed(() => {
    const sold = keysSold.value.toLocaleString('en-US')
    const total = TOTAL_KEYS_FOR_SALE.toLocaleString('en-US')
    return `${sold} / ${total}`
  })

  /* --- User Activity Simulation --- */
  const activeToasts = ref([])
  let toastIdCounter = 0

  // Generate random ETH address
  function generateRandomAddress() {
    const chars = '0123456789abcdef'
    let address = '0x'
    for (let i = 0; i < 40; i++) {
      address += chars[Math.floor(Math.random() * chars.length)]
    }
    return address
  }

  // Format address for display (show first 6 and last 4 chars)
  function formatAddressForToast(address) {
    return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`
  }

  // Add a new toast notification
  function addUserActivityToast() {
    const fullAddress = generateRandomAddress()
    const toast = {
      id: toastIdCounter++,
      address: formatAddressForToast(fullAddress),
      fullAddress: fullAddress
    }
    
    activeToasts.value.push(toast)
    
    // Remove toast after 4 seconds
    setTimeout(() => {
      const index = activeToasts.value.findIndex(t => t.id === toast.id)
      if (index !== -1) {
        activeToasts.value.splice(index, 1)
      }
    }, 4000)
  }

  // Start the random user activity simulation
  function startUserActivitySimulation() {
    // Initial delay before first toast (2 seconds)
    setTimeout(() => {
      addUserActivityToast()
      
      // Then show toasts at random intervals (loops infinitely)
      function scheduleNextToast() {
        // Random interval between 5-13 seconds
        const nextInterval = Math.random() * 6000 + 4000
        
        setTimeout(() => {
          // 70% chance to show a toast
          if (Math.random() > 0.3) {
            addUserActivityToast()
          }
          // Schedule the next one (infinite loop)
          scheduleNextToast()
        }, nextInterval)
      }
      
      // Start the loop
      scheduleNextToast()
    }, 2000) // Initial 2 second delay
  }

  const canClaim = computed(() => (pending.value ?? 0n) > 0n)
  const pendingUSDTDisplay = computed(() => {
    const n = Number(ethers.formatUnits(pending.value ?? 0n, usdtDecimals.value))
    const safe = Number.isFinite(n) ? n : 0
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(safe)
  })

  /* --- Global payout header --- */
  const monthlySalesData = ref([])
  const isFetchingSales = ref(true) // Start as true to show loading on initial render
  const SALES_API_URL = 'https://api.starhorizon.net/api/v1/app-sales/month'
  
  // Calculate cumulative sales for the current payout period
  const PAYOUT_AMOUNT_USD = computed(() => {
    
    if (!monthlySalesData.value || monthlySalesData.value.length === 0) return 0
    
    const now = new Date()
    const firstPayoutDate = new Date(2026, 0, 31, 23, 59, 59) // January 31st, 2026
    const firstPeriodStart = new Date(2025, 11, 1, 0, 0, 0) // December 1st, 2025
    
    // Determine the current period start and end dates
    let periodStart, periodEnd
    
    if (now < firstPayoutDate) {
      // First period: Dec 1, 2025 - Jan 31, 2026
      periodStart = firstPeriodStart
      periodEnd = firstPayoutDate
    } else {
      // Subsequent periods: first day to last day of current month
      periodStart = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0)
      periodEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59)
    }
    
    // Sum up sales for current period
    const total = monthlySalesData.value.reduce((sum, sale) => {
      const saleDate = new Date(sale.sale_day)
      
      // Only count sales within the current period
      if (saleDate >= periodStart && saleDate <= periodEnd) {
        return sum + parseFloat(sale.total_sales || 0)
      }
      return sum
    }, 0)
    
    return total
  })
  
  const globalPayoutAmountDisplay = computed(() =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(PAYOUT_AMOUNT_USD.value)
  )
  
  // Calculate payout date
  const payoutDate = computed(() => {
    const now = new Date()
    const firstPayoutDate = new Date(2026, 0, 31, 23, 59, 59) // January 31st, 2026 at 23:59:59
    
    // If we're before the first payout date, return January 31st, 2026
    if (now < firstPayoutDate) {
      return firstPayoutDate
    }
    
    // Otherwise, return the last day of current month at 23:59:59
    return new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59)
  })
  
  // Calculate payout start date
  const payoutStartDate = computed(() => {
    const now = new Date()
    const firstPayoutDate = new Date(2026, 0, 31, 23, 59, 59) // January 31st, 2026
    const firstPayoutStart = new Date(2025, 11, 1, 0, 0, 0) // December 1st, 2025
    
    // If we're before the first payout date, return December 1st, 2025 (start of first period)
    if (now < firstPayoutDate) {
      return firstPayoutStart
    }
    
    // Otherwise, return the first day of current month
    return new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0)
  })
  
  const daysUntilPayout = computed(() => {
    const now = Date.now()
    const end = payoutDate.value.getTime()
    const diff = Math.max(0, end - now)
    return Math.ceil(diff / 86_400_000)
  })
  
  // Fetch sales data from API
  async function fetchMonthlySales() {
    // Skip if already fetching (but not on initial load)
    if (isFetchingSales.value && monthlySalesData.value.length > 0) return
    
    try {
      isFetchingSales.value = true
      const response = await fetch(SALES_API_URL)
      
      if (!response.ok) {
        throw new Error(`API request failed: ${response.status}`)
      }
      
      const jsonData = await response.json()
      
      if (jsonData && jsonData.data && Array.isArray(jsonData.data)) {
        monthlySalesData.value = jsonData.data
      } else {
        console.warn('Unexpected API response format:', jsonData)
        monthlySalesData.value = []
      }
    } catch (error) {
      console.error('Failed to fetch monthly sales:', error)
      // Keep existing data on error, or set to empty array if first fetch
      if (monthlySalesData.value.length === 0) {
        monthlySalesData.value = []
      }
    } finally {
      isFetchingSales.value = false
    }
  }
  function formatDdMmYyyyHhMmAm(d) {
    const dd = String(d.getDate()).padStart(2, '0')
    const mm = String(d.getMonth() + 1).padStart(2, '0')
    const yyyy = d.getFullYear()
    let h = d.getHours()
    const m = String(d.getMinutes()).padStart(2, '0')
    const ampm = h >= 12 ? 'PM' : 'AM'
    h = h % 12; if (h === 0) h = 12
    return `${dd}/${mm}/${yyyy}, ${h}:${m} ${ampm}`
  }
  const totalStakedDisplay = computed(() => {
    const n = Number(ethers.formatUnits(totalStakedVRXChain.value ?? 0n, 18))
    return n.toLocaleString('en-US', { maximumFractionDigits: 4 })
  })
  const mySharesDisplay = computed(() => {
    const v = stakedVRX.value ?? 0n
    const k = stakedKeys.value ?? 0n
    const lockMult = [1, 2, 3, 6, 12][Number(lockIdx.value) || 0] || 1
    const keyMult = (k === 0n) ? 1n : 2n * k
    const raw = v * keyMult * BigInt(lockMult)
    const asNum = Number(ethers.formatUnits(raw, 18))
    return asNum.toLocaleString('en-US', { maximumFractionDigits: 2 })
  })
  const ownershipPctDisplay = computed(() => {
    const mine = Number(ethers.formatUnits(stakedVRX.value ?? 0n, 18))
    const tot  = Number(ethers.formatUnits(totalStakedVRXChain.value ?? 0n, 18))
    const pct = tot > 0 ? (mine / tot) * 100 : 0
    return pct.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '%'
  })
  const payoutDateLabel = computed(() => formatDdMmYyyyHhMmAm(payoutDate.value))
  const payoutProgressStyle = computed(() => {
    const start = payoutStartDate.value.getTime()
    const end   = payoutDate.value.getTime()
    const now   = Date.now()
    const total = Math.max(1, end - start)
    const done  = Math.min(Math.max(now - start, 0), end - start)
    const pct   = Math.min(100, Math.max(0, (done / total) * 100))
    return `${pct.toFixed(2)}%`
  })
  
  // Set up periodic refresh of sales data (every 5 minutes)
  let salesRefreshInterval = null
  function startSalesRefreshInterval() {
    // Initial fetch
    fetchMonthlySales()
    
    // Refresh every 5 minutes
    if (salesRefreshInterval) clearInterval(salesRefreshInterval)
    salesRefreshInterval = setInterval(() => {
      fetchMonthlySales()
    }, 5 * 60 * 1000) // 5 minutes
  }
  
  function stopSalesRefreshInterval() {
    if (salesRefreshInterval) {
      clearInterval(salesRefreshInterval)
      salesRefreshInterval = null
    }
  }
  
  // Generate chart data for sales visualization
  const salesChartData = computed(() => {
    if (!monthlySalesData.value || monthlySalesData.value.length === 0) {
      return { pathArea: '', pathLine: '' }
    }
    
    const now = new Date()
    const firstPayoutDate = new Date(2026, 0, 31, 23, 59, 59) // January 31st, 2026
    const firstPeriodStart = new Date(2025, 11, 1, 0, 0, 0) // December 1st, 2025
    
    // Determine the current period start and end dates
    let periodStart, periodEnd
    
    if (now < firstPayoutDate) {
      // First period: Dec 1, 2025 - Jan 31, 2026
      periodStart = firstPeriodStart
      periodEnd = firstPayoutDate
    } else {
      // Subsequent periods: first day to last day of current month
      periodStart = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0)
      periodEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59)
    }
    
    // Filter sales for current period
    const periodSales = monthlySalesData.value
      .filter(sale => {
        const saleDate = new Date(sale.sale_day)
        return saleDate >= periodStart && saleDate <= periodEnd
      })
    
    if (periodSales.length === 0) {
      return { pathArea: '', pathLine: '' }
    }
    
    // Create a map of sales by day (day of month)
    const salesByDay = {}
    periodSales.forEach(sale => {
      const saleDate = new Date(sale.sale_day)
      const day = saleDate.getDate()
      salesByDay[day] = parseFloat(sale.total_sales || 0)
    })
    
    // Calculate total days in the period
    const totalDaysInPeriod = Math.ceil((periodEnd - periodStart) / (1000 * 60 * 60 * 24)) + 1
    
    // Get the current day within the period (how many days into the period we are)
    const daysIntoPeriod = Math.min(
      Math.ceil((now - periodStart) / (1000 * 60 * 60 * 24)) + 1,
      totalDaysInPeriod
    )
    const maxDay = daysIntoPeriod
    
    // Build complete day-by-day data with cumulative values
    const chartPoints = []
    let cumulative = 0
    
    // Build chart points by iterating through each day in the period
    for (let dayIndex = 1; dayIndex <= maxDay; dayIndex++) {
      // Calculate the actual date for this day index
      const currentDate = new Date(periodStart)
      currentDate.setDate(currentDate.getDate() + (dayIndex - 1))
      const dayOfMonth = currentDate.getDate()
      
      // Add any sales for this day to cumulative
      if (salesByDay[dayOfMonth]) {
        cumulative += salesByDay[dayOfMonth]
      }
      
      chartPoints.push({
        day: dayIndex, // Use day index within period, not day of month
        value: cumulative
      })
    }
    
    if (chartPoints.length === 0) {
      return { pathArea: '', pathLine: '' }
    }
    
    // Chart dimensions (percentage based for responsiveness)
    const W = 100
    const H = 100
    
    // Scaling
    const minDay = 1
    const maxDayInChart = Math.max(maxDay, 7) // Show at least 7 days for better visual
    const dayRange = maxDayInChart - minDay
    const maxValue = Math.max(...chartPoints.map(p => p.value), 1) // Avoid division by zero
    
    // Add padding at top (use 85% of height for chart, 15% padding at top)
    const chartHeight = H * 0.85
    const topPadding = H * 0.15
    
    // Convert to SVG coordinates (0,0 is top-left, 100,100 is bottom-right)
    const svgPoints = chartPoints.map(p => {
      const x = ((p.day - minDay) / dayRange) * W
      // Map value to Y coordinate with top padding
      // 0 value = bottom (H), max value = top padding (topPadding)
      const y = H - (p.value / maxValue) * chartHeight
      return { x: x.toFixed(2), y: y.toFixed(2) }
    })
    
    if (svgPoints.length === 0) {
      return { pathArea: '', pathLine: '' }
    }
    
    // Generate smooth SVG path for line
    const pathLine = svgPoints.reduce((acc, p, i) => {
      if (i === 0) {
        return `M ${p.x} ${p.y}`
      }
      return acc + ` L ${p.x} ${p.y}`
    }, '')
    
    // Generate SVG path for area (filled to bottom)
    const firstPoint = svgPoints[0]
    const lastPoint = svgPoints[svgPoints.length - 1]
    
    let pathArea = `M ${firstPoint.x} ${H}` // Start at bottom-left
    pathArea += ` L ${firstPoint.x} ${firstPoint.y}` // Go up to first data point
    
    // Draw through all data points
    for (let i = 1; i < svgPoints.length; i++) {
      pathArea += ` L ${svgPoints[i].x} ${svgPoints[i].y}`
    }
    
    // Close the path along the bottom
    pathArea += ` L ${lastPoint.x} ${H} Z`
    
    return { pathArea, pathLine }
  })

  const hasActiveStake = computed(() => (stakedVRX.value > 0n || stakedKeys.value > 0n))
  const lockStartTs = ref(0)
  const lockEndTs   = ref(0)
  const hasStake = computed(() => (stakedVRX.value > 0n || stakedKeys.value > 0n))
  const canUnstake = computed(() => hasStake.value && Number(lockSecondsLeft.value ?? 0) <= 0)

  const LOCK_DAYS  = [30, 60, 90, 180, 360]
  const LOCK_MULTS = [1, 2, 3, 6, 12]
  const lockLenDays = computed(() => LOCK_DAYS[Number(lockIdx.value)] ?? 0)
  const lockMult    = computed(() => LOCK_MULTS[Number(lockIdx.value)] ?? 1)

  const mySharesRowDisplay = computed(() => {
    const vrx = Number(ethers.formatUnits(stakedVRX.value ?? 0n, 18))
    const k   = Number(stakedKeys.value ?? 0n)
    const keysFactor = k === 0 ? 1 : (2 * k)
    const mult = lockMult.value || 1
    const v = vrx * keysFactor * mult
    return v.toLocaleString('en-US', { maximumFractionDigits: 4 })
  })

  function fmtDMYhm(ts) {
    if (!ts) return '—'
    return new Date(ts * 1000).toLocaleString('en-GB', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true
    })
  }

  const ethBalWei = ref(0n)
  const ethBalDisplay = computed(() => {
    const n = Number(ethers.formatEther(ethBalWei.value ?? 0n))
    return n.toLocaleString('en-US', { maximumFractionDigits: 4 })
  })
  const usdtBalDisplay = computed(() => {
    const n = Number(ethers.formatUnits(usdtBal.value ?? 0n, usdtDecimals.value))
    return n.toLocaleString('en-US', { maximumFractionDigits: 2 })
  })
  const lockProgressPct = computed(() => {
    const start = Number(lockStartTs.value || 0)
    const end   = Number(lockEndTs.value   || 0)
    if (!start || !end || end <= start) return 0
    const now = Math.floor(Date.now() / 1000)
    const num = Math.min(now, end) - start
    const den = end - start
    return Math.max(0, Math.min(100, (num / den) * 100))
  })

  /* ---- Snapshots for chart ---- */
  const snaps = ref({ ts: [], vrx: [], keys: [], weight: [] })
  const priceVRXDisplay2dp = computed(() => {
    const n = Number(ethers.formatUnits(priceVRX.value ?? 0n, usdtDecimals.value))
    const safe = Number.isFinite(n) ? n : 0
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(safe)
  })
  const CHART_W = 1000, CHART_H = 280, PAD_L = 40, PAD_R = 20, PAD_T = 20, PAD_B = 30
  const chartPointsPx = computed(() => {
    const rows = (snaps.value.ts || [])
      .map((t, i) => ({ t, v: snaps.value.vrx?.[i] ?? 0 }))
      .filter(r => r.t > 0)
      .sort((a, b) => a.t - b.t)
    if (rows.length === 0) return []
    const tMin = rows[0].t, tMax = rows[rows.length - 1].t
    const vMin = Math.min(...rows.map(r => r.v))
    const vMax = Math.max(...rows.map(r => r.v))
    const xRange = Math.max(1, tMax - tMin)
    const yRange = Math.max(1e-12, vMax - vMin)
    const W = CHART_W - PAD_L - PAD_R
    const H = CHART_H - PAD_T - PAD_B
    return rows.map(r => {
      const nx = (r.t - tMin) / xRange
      const ny = (r.v - vMin) / yRange
      const x = PAD_L + nx * W
      const y = PAD_T + (1 - ny) * H
      return { x, y }
    })
  })
  const chartPathLine = computed(() => {
    const pts = chartPointsPx.value
    if (pts.length === 0) return ''
    return pts.reduce((acc, p, i) => acc + (i ? ` L ${p.x.toFixed(2)} ${p.y.toFixed(2)}` : `M ${p.x.toFixed(2)} ${p.y.toFixed(2)}`), '')
  })
  const chartPathArea = computed(() => {
    const pts = chartPointsPx.value
    if (pts.length === 0) return ''
    const baseY = CHART_H - PAD_B
    const head = `M ${pts[0].x.toFixed(2)} ${baseY} L ${pts[0].x.toFixed(2)} ${pts[0].y.toFixed(2)}`
    const body = pts.slice(1).map(p => `L ${p.x.toFixed(2)} ${p.y.toFixed(2)}`).join(' ')
    const tail = `L ${pts[pts.length - 1].x.toFixed(2)} ${baseY} Z`
    return `${head} ${body} ${tail}`
  })
  const chartSummary = computed(() => {
    const rows = (snaps.value.ts || [])
      .map((t, i) => ({ t, v: snaps.value.vrx?.[i] ?? 0 }))
      .filter(r => r.t > 0)
      .sort((a, b) => a.t - b.t)
    if (rows.length === 0) return { startDate: '—', endDate: '—', totalValue: '0' }
    const fmt = (ts) => fmtDMY(ts)
    const latest = rows[rows.length - 1]?.v ?? 0
    const totalStr = latest.toLocaleString(undefined, { maximumFractionDigits: 4 })
    return { startDate: fmt(rows[0].t), endDate: fmt(rows[rows.length - 1].t), totalValue: totalStr }
  })

  const STAGE_TOTAL_TOKENS = 2_000_000
  const remainingThisStage = computed(() => {
    const rem = Number(ethers.formatUnits(remainingVRXForSale.value ?? 0n, 18))
    return Math.max(0, Math.min(STAGE_TOTAL_TOKENS, rem))
  })
  const soldThisStage = computed(() => Math.max(0, STAGE_TOTAL_TOKENS - remainingThisStage.value))
  const percentSold = computed(() => Math.max(0, Math.min(100, (soldThisStage.value / STAGE_TOTAL_TOKENS) * 100)))
  const percentSoldStyle = computed(() => saleDataReady.value ? `${percentSold.value}%` : '0%')
  const priceKEYDisplay2dp = computed(() => {
    const n = Number(ethers.formatUnits(priceKEY.value ?? 0n, usdtDecimals.value))
    const safe = Number.isFinite(n) ? n : 0
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(safe)
  })
  const soldRemainingLabel = computed(() => {
    if (!saleDataReady.value) return '0 / 2,000,000'
    const sold = soldThisStage.value.toLocaleString('en-US')
    const rem  = remainingThisStage.value.toLocaleString('en-US')
    const tot = STAGE_TOTAL_TOKENS.toLocaleString('en-US')
    return `${sold} / ${tot}`
  })

  function buySingleKey() {
    buyVrxInput.value = '0'
    buyKeyInput.value = '1'
    ensureUSDTAllowanceAndBuyBundle(false)
  }
  function refreshInBackground() {
    Promise.resolve().then(() => refreshAll(true)).catch(err => console.error('Background refresh failed:', err))
  }

  function formatVRX(bi) {
    try {
      return Number(ethers.formatUnits(bi ?? 0n, 18)).toLocaleString('en-US', { maximumFractionDigits: 4 })
    } catch { return '0' }
  }
  function sleep(ms) { return new Promise(r => setTimeout(r, ms)) }
  const keysMultiplier = computed(() => {
    const k = Number(stakedKeys.value ?? 0n)
    const mult = Math.max(1, k * 2)
    return `${mult}X`
  })
  async function getLogsWithRetry(filter) {
    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      try { return await readProvider.getLogs(filter) } catch (e) {
        const msg = e?.info?.error?.message || e?.message || ''
        const tooMany = msg.includes('Too Many Requests') || e?.code === 'SERVER_ERROR'
        const timeout  = msg.toLowerCase().includes('timeout') || e?.code === 'TIMEOUT'
        if (tooMany || timeout) { const delay = 400 * 2 ** attempt; await sleep(delay); continue }
        throw e
      }
    }
    throw new Error('getLogs retry limit exceeded')
  }

  let holdersInited = false
  // Blacklist for treasury wallets and contracts that should not appear on leaderboard
  const BLACKLIST_ADDRESSES = [
    '0x0fddf3E36650DCfD8132da30C13562565c16Cc05', // Treasury/Contract
    '0x01DA939D1bdb832DFE7828545F06eb6B575faD01', // Treasury/Contract
  ].map(addr => addr.toLowerCase())
  
  async function fetchTopVRXHolders() {
    if (!VRX_ADDR.value) return
    holdersLoading.value = true
    holdersError.value = ''
    try {
      const url = new URL(`${MORALIS_BASE}/erc20/${VRX_ADDR.value}/owners`)
      url.searchParams.set('chain', MORALIS_CHAIN)
      url.searchParams.set('limit', '100')
      url.searchParams.set('order', 'DESC')
      const res = await fetch(url.toString(), {
        headers: { 'accept': 'application/json', 'X-API-Key': MORALIS_API_KEY || 'PASTE_YOUR_MORALIS_KEY_HERE' }
      })
      const data = await res.json()
      if (!res.ok || !data?.result) throw new Error(data?.message || data?.error || 'Moralis owners error')
      const SELF = String(VRX_ADDR.value).toLowerCase()
      const excludeContracts = false
      const rows = (data.result || [])
        .filter(r => r?.owner_address)
        .filter(r => String(r.owner_address).toLowerCase() !== SELF)
        .filter(r => !BLACKLIST_ADDRESSES.includes(String(r.owner_address).toLowerCase()))
        .filter(r => (excludeContracts ? !r.is_contract : true))
        .map(r => ({ address: r.owner_address, balance: BigInt(r.balance ?? '0') }))
        .sort((a, b) => (a.balance === b.balance ? 0 : (a.balance > b.balance ? -1 : 1)))
        .slice(0, TOP_N)
      topHolders.value = rows

      try {
        const statsUrl = new URL(`${MORALIS_BASE}/erc20/${VRX_ADDR.value}/holders`)
        statsUrl.searchParams.set('chain', MORALIS_CHAIN)
        const resStats = await fetch(statsUrl.toString(), {
          headers: { 'accept': 'application/json', 'X-API-Key': MORALIS_API_KEY || 'PASTE_YOUR_MORALIS_KEY_HERE' }
        })
        const stats = await resStats.json()
        if (resStats.ok && typeof stats?.totalHolders === 'number') totalVRXHolders.value = stats.totalHolders
        else totalVRXHolders.value = rows.length
      } catch {
        totalVRXHolders.value = rows.length
      }
    } catch (e) {
      console.error('fetchTopVRXHolders (Moralis):', e)
      holdersError.value = 'Unable to load holders right now. Please try again shortly.'
    } finally { holdersLoading.value = false }
  }

  const totalVRXHoldersDisplay = computed(() => totalVRXHolders.value.toLocaleString('en-US'))
  function shortAddr(addr) {
    if (!addr) return ''
    const s = String(addr); const has0x = s.startsWith('0x') || s.startsWith('0X'); const body = has0x ? s.slice(2) : s
    if (body.length <= 13) return s
    const head = body.slice(0, 5); const tail = body.slice(-8)
    return (has0x ? '0x' : '') + head + '...' + tail
  }
  async function fetchSnapshots() {
    try {
      const [tsArr, vrxArr, keyArr, wArr] = await coreRead.value.getSnapshots()
      const ts = tsArr.map(t => Number(t))
      const vrx = vrxArr.map(v => Number(ethers.formatUnits(v, 18)))
      const keys = keyArr.map(k => Number(k))
      const weight = wArr.map(w => Number(w))
      snaps.value = { ts, vrx, keys, weight }
    } catch (e) {
      console.warn('fetchSnapshots failed:', e)
      snaps.value = { ts: [], vrx: [], keys: [], weight: [] }
    }
  }

  /* ---------------- Utils ---------------- */
  const short = (addr) => addr ? addr.slice(0,6)+'...'+addr.slice(-4) : ''
  const fmt18 = (x) => Number(ethers.formatUnits(x ?? 0n, 18)).toLocaleString()
  const fmt0  = (x) => (x ?? 0n).toString()
  const fmtUSDT = (x) => Number(ethers.formatUnits(x ?? 0n, usdtDecimals.value)).toLocaleString()
  const ONE_18 = 10n ** 18n
  function toUnitsSafe(val, decimals) {
    let s = String(val ?? '').trim().replace(/,/g, '')
    if (s === '' || s === '.') s = '0'
    if (s.endsWith('.')) s += '0'
    if (!/^(\d+)(\.\d+)?$/.test(s)) s = '0'
    return ethers.parseUnits(s, decimals)
  }
  const lockOptions = [
    { label: '30d (×1)', mult: 1 },
    { label: '60d (×2)', mult: 2 },
    { label: '90d (×3)', mult: 3 },
    { label: '180d (×6)', mult: 6 },
    { label: '360d (×12)', mult: 12 },
  ]
  const priceVRXDisplay = computed(() => Number(ethers.formatUnits(priceVRX.value, usdtDecimals.value)).toLocaleString())
  const priceKEYDisplay = computed(() => Number(ethers.formatUnits(priceKEY.value, usdtDecimals.value)).toLocaleString())
  const previewCostVRXDisplay = computed(() => {
    const amt = toUnitsSafe(buyVrxInput.value, 18)
    const c = (amt * priceVRX.value) / ONE_18
    return Number(ethers.formatUnits(c, usdtDecimals.value)).toLocaleString()
  })
  const previewCostKEYDisplay = computed(() => {
    const amt = toUnitsSafe(buyKeyInput.value, 0)
    const c = amt * priceKEY.value
    return Number(ethers.formatUnits(c, usdtDecimals.value)).toLocaleString()
  })
  const maxVRXCanBuy = computed(() => {
    // Calculate max VRX based on USDT balance and VRX price
    // maxVRX (18 decimals) = (usdtBal * 10^18) / priceVRX
    if (!priceVRX.value || priceVRX.value === 0n) return 0
    try {
      const maxVRXBigInt = (usdtBal.value * ONE_18) / priceVRX.value
      const maxFromBalance = Number(ethers.formatUnits(maxVRXBigInt, 18))
      
      // Also limit by remaining VRX available for sale
      let maxFromSale = Number(ethers.formatUnits(remainingVRXForSale.value ?? 0n, 18))
      
      // If first-time buyer (2x bonus), they can only buy half of what's remaining
      // because they'll receive double the tokens
      if (bonusAvailable.value === true) {
        maxFromSale = maxFromSale / 2
      }
      
      // Return the minimum of what user can afford and what's available
      return Math.min(maxFromBalance, maxFromSale)
    } catch {
      return 0
    }
  })
  const previewCostBundleDisplay = computed(() => {
    const c1 = (toUnitsSafe(buyVrxInput.value, 18) * priceVRX.value) / ONE_18
    const c2 = toUnitsSafe(buyKeyInput.value, 0) * priceKEY.value
    const total = c1 + c2
    return Number(ethers.formatUnits(total, usdtDecimals.value)).toLocaleString()
  })
  const lockHuman = computed(() => {
    if (lockSecondsLeft.value == null) return '—'
    const s = Number(lockSecondsLeft.value)
    if (s <= 0) return 'Unlocked'
    const d = Math.floor(s / 86400)
    const h = Math.floor((s % 86400) / 3600)
    const m = Math.floor((s % 3600) / 60)
    if (d > 0) return `${d}d ${h}h`
    if (h > 0) return `${h}h ${m}m`
    return `${m}m`
  })
  function fmtDMY(input) {
    const d = (input instanceof Date)
      ? input
      : new Date(typeof input === 'number'
          ? (input > 1e12 ? input : input * 1000)
          : input)
    const dd = String(d.getDate()).padStart(2, '0')
    const mm = String(d.getMonth() + 1).padStart(2, '0')
    const yyyy = d.getFullYear()
    return `${dd}/${mm}/${yyyy}`
  }

  /* ---------------- Init (read-only) ---------------- */
  let inited = false
  async function initRead() {
    if (inited) return
    coreRead.value = new ethers.Contract(CORE_ADDR.value, CORE_ABI, readProvider)
    const usdtAddr = await coreRead.value.usdt()
    USDT_ADDR.value = usdtAddr
    usdtRead.value = new ethers.Contract(USDT_ADDR.value, ERC20_ABI, readProvider)
    try { usdtDecimals.value = await usdtRead.value.decimals() } catch { usdtDecimals.value = 6 }
    const vrxAddr = await coreRead.value.vrx(); VRX_ADDR.value = vrxAddr
    vrxRead.value = new ethers.Contract(VRX_ADDR.value, ERC20_ABI, readProvider)
    const keyAddr = await coreRead.value.key(); KEY_ADDR.value = keyAddr
    keyRead.value = new ethers.Contract(KEY_ADDR.value, ERC20_ABI, readProvider)
    vrxAdminRead = new ethers.Contract(VRX_ADDR.value, VRX_ADMIN_ABI, readProvider)

    inited = true
  }
  async function loadGlobals() {
    priceVRX.value = await coreRead.value.priceUSDTPerVRX()
    priceKEY.value = await coreRead.value.priceUSDTPerKEY()
    totalStakedVRXChain.value = await coreRead.value.userStakedTotal()
    remainingVRXForSale.value = await coreRead.value.remainingVRXForSale()
    remainingKEYForSale.value = await coreRead.value.remainingKEYForSale()
    saleDataReady.value = true
  }
  async function loadMyStuff() {
    if (!account.value) return
    ethBalWei.value = await readProvider.getBalance(account.value)
    usdtBal.value = await usdtRead.value.balanceOf(account.value)
    vrxBal.value  = await vrxRead.value.balanceOf(account.value)
    keyBal.value  = await keyRead.value.balanceOf(account.value)
    try {
      const u = await coreRead.value.userInfo(account.value)
      stakedVRX.value  = u.vrxStaked
      stakedKeys.value = u.keysStaked
      lockIdx.value    = u.lockIndex
      try {
        const d = await coreRead.value.lockDetails(account.value)
        lockStartTs.value     = Number(d.lockStart ?? d[0] ?? 0)
        lockEndTs.value       = Number(d.lockEnd   ?? d[1] ?? 0)
        lockIdx.value         = Number(d.lockIndex ?? d[2] ?? lockIdx.value)
        lockSecondsLeft.value = Number(d.secondsLeft ?? d[3] ?? lockSecondsLeft.value ?? 0)
      } catch {
        const durSec = (LOCK_DAYS[Number(lockIdx.value)] ?? 0) * 24 * 3600
      }
    } catch {
      stakedVRX.value = 0n; stakedKeys.value = 0n; lockIdx.value = 0
    }
    try { pending.value = await coreRead.value.pendingRewards(account.value) } catch { pending.value = 0n }
    try { const seconds = await coreRead.value.secondsUntilUnlock(account.value); lockSecondsLeft.value = Number(seconds) } catch { lockSecondsLeft.value = null }

    await refreshFirstBuyFlag()
    await refreshVRXPreview()
  }
  watch([buyVrxInput, account], () => { refreshVRXPreview() })
  onMounted(async () => {
    await initRead()
    await loadGlobals()
    await fetchTopVRXHolders()
    await fetchSnapshots()
    await refreshVestingPool()
    await refreshVestedInfo()
    
    // Start user activity simulation
    startUserActivitySimulation()
    
    // Start fetching sales data
    startSalesRefreshInterval()
    
    if (window.ethereum?.on) {
      window.ethereum.on('accountsChanged', (accs) => { account.value = accs?.[0] || ''; refreshAll() })
      window.ethereum.on('chainChanged', () => window.location.reload())
    }
  })
  
  onUnmounted(() => {
    // Clean up interval on unmount
    stopSalesRefreshInterval()
  })

  /* ---------------- MetaMask helpers ---------------- */
  function isMobile() {
    if (typeof navigator === 'undefined') return false
    return /android|iphone|ipad|ipod|opera mini|iemobile|blackberry/i.test(navigator.userAgent)
  }

  /*
  function getDappUrl() {
    if (typeof window === 'undefined') return ''
    const { origin, pathname, search } = window.location
    // drop hash so it matches what MetaMask expects
    return `${origin}${pathname}${search || ''}`
  }*/

  function getDappUrl() {
    if (typeof window === 'undefined') {
      return 'staking.starvara.com'
    }

    const { host, pathname } = window.location
    // e.g. "staking.starvara.com" or "staking.starvara.com/some/path"
    return `${host}${pathname}`
  }

  
  const MAINNET_HEX = '0x1'
  async function getMM() {
    // 1) No injected provider?
    if (typeof window === 'undefined' || !window.ethereum) {
      // On mobile, deep-link into MetaMask app
      if (isMobile()) {
        // IMPORTANT: no encodeURIComponent, no protocol
        let dappUrl = getDappUrl()
        dappUrl = dappUrl.replace(/^https?:\/\//, '') // just in case

        const link = `https://metamask.app.link/dapp/${dappUrl}`

        alert('Opening MetaMask to connect your wallet...')
        window.location.href = link
        throw new Error('Redirecting to MetaMask mobile')
      }

      // Desktop: normal "install MetaMask" flow
      alert('Please install MetaMask')
      throw new Error('No MetaMask')
    }

    // 2) If multiple providers exist (e.g. Brave + MetaMask), pick MetaMask
    let eth = window.ethereum
    if (eth.providers?.length) {
      const mm = eth.providers.find(p => p.isMetaMask)
      if (mm) eth = mm
    }

    // 3) Request accounts
    await eth.request({ method: 'eth_requestAccounts' })

    // 4) Ensure we’re on Mainnet
    const chain = await eth.request({ method: 'eth_chainId' })
    if (chain !== MAINNET_HEX) {
      try {
        await eth.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: MAINNET_HEX }]
        })
      } catch (err) {
        if (err?.code === 4902) {
          // Chain not added yet
          await eth.request({
            method: 'wallet_addEthereumChain',
            params: [{
              chainId: MAINNET_HEX,
              chainName: 'Ethereum Mainnet',
              nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
              rpcUrls: [RPC],
              blockExplorerUrls: ['https://etherscan.io'],
            }]
          })
          await eth.request({
            method: 'wallet_switchEthereumChain',
            params: [{ chainId: MAINNET_HEX }]
          })
        } else {
          throw err
        }
      }
    }

    // 5) Build ethers provider + signer using the chosen 'eth'
    const provider = new ethers.BrowserProvider(eth)
    const signer = await provider.getSigner()
    account.value = await signer.getAddress()

    const coreW = new ethers.Contract(CORE_ADDR.value, CORE_ABI, signer)
    const usdtW = new ethers.Contract(USDT_ADDR.value, ERC20_ABI, signer)
    const vrxW  = new ethers.Contract(VRX_ADDR.value,  ERC20_ABI, signer)
    const keyW  = new ethers.Contract(KEY_ADDR.value,  ERC20_ABI, signer)
    const vrxAdminW = new ethers.Contract(VRX_ADDR.value, VRX_ADMIN_ABI, signer)

    return { signer, coreW, usdtW, vrxW, keyW, vrxAdminW }
  }

  /* ---------------- Connect / Refresh ---------------- */
  async function connect() {
    try { 
      isLoadingBalances.value = true
      await initRead(); 
      await getMM(); 
      await refreshAll() 
    }
    catch (e) { 
      console.error('Connect error:', e); 
      alert((e && (e.message || e.shortMessage)) || 'Failed to connect wallet') 
    }
    finally {
      isLoadingBalances.value = false
    }
  }
  async function refreshAll(silent = false) {
    try {
      await initRead()
      await loadGlobals()
      await fetchTopVRXHolders()
      await fetchSnapshots()
      if (account.value) await loadMyStuff()
    } catch (e) {
      console.error(e)
      if (!silent) showError('Refresh failed. Check console.')
    }
  }

  async function ensureUSDTAllowanceAndBuyBundle(stakeAfter) {
    try {
      await initRead()
      const { coreW, usdtW } = await getMM()
      const vrxAmt18 = toUnitsSafe(buyVrxInput.value, 18)
      const keyAmt0  = toUnitsSafe(buyKeyInput.value, 0)
      if (vrxAmt18 <= 0n && keyAmt0 <= 0n) return
      showLoading('Calculating cost...')
      const costVRX = vrxAmt18 > 0n ? await coreRead.value.previewVRXCost(vrxAmt18) : 0n
      const costKEY = keyAmt0  > 0n ? await coreRead.value.previewKEYCost(keyAmt0)   : 0n
      const totalCost = costVRX + costKEY
      if (totalCost <= 0n) { hideTx(); return }
      const allowance = await usdtRead.value.allowance(account.value, CORE_ADDR.value)
      if (allowance < totalCost) {
        showLoading('Approving USDT...')
        const tx = await usdtW.approve(CORE_ADDR.value, totalCost)
        await tx.wait()
      }
      if (!stakeAfter) {
        showLoading('Buying...')
        const tx2 = await coreW.buyBundle(vrxAmt18, keyAmt0)
        await tx2.wait()
        showSuccess('Buy complete.')
        refreshInBackground()
      } else {
        showLoading('Buying & Staking...')
        const idx = Number(buyLockIndex.value)
        const tx2 = await coreW.buyAndStakeBundle(vrxAmt18, keyAmt0, idx)
        await tx2.wait()
        showSuccess('Buy & Stake complete.')
        refreshInBackground()
      }
    } catch (e) { console.error(e); showError(extractErr(e)) }
  }

  async function ensureAllowancesAndStake() {
    try {
      await initRead()
      const { coreW, vrxW, keyW } = await getMM()
      const addVrx18 = toUnitsSafe(stakeVrxInput.value, 18)
      const addKey0  = toUnitsSafe(stakeKeyInput.value, 0)
      if (addVrx18 <= 0n && addKey0 <= 0n) return
      if (addVrx18 > 0n) {
        const allowance = await vrxRead.value.allowance(account.value, CORE_ADDR.value)
        if (allowance < addVrx18) {
          showLoading('Approving VRX...')
          const tx = await vrxW.approve(CORE_ADDR.value, addVrx18)
          await tx.wait()
        }
      }
      if (addKey0 > 0n) {
        const allowanceK = await keyRead.value.allowance(account.value, CORE_ADDR.value)
        if (allowanceK < addKey0) {
          showLoading('Approving Keys...')
          const tx = await keyW.approve(CORE_ADDR.value, addKey0)
          await tx.wait()
        }
      }
      showLoading('Staking...')
      const idx = Number(stakeLockIndex.value)
      const tx2 = await coreW.stake(addVrx18, addKey0, idx)
      await tx2.wait()
      showSuccess('Stake complete.')
      refreshInBackground()
    } catch (e) { console.error(e); showError(extractErr(e)) }
  }

  async function unstakeAll() {
    try {
      await initRead()
      const { coreW } = await getMM()
      showLoading('Unstaking...')
      const tx = await coreW.unstakeAll()
      await tx.wait()
      showSuccess('Unstake complete.')
      refreshInBackground()
    } catch (e) { console.error(e); showError(extractErr(e)) }
  }

  async function claim() {
    try {
      await initRead()
      const { coreW } = await getMM()
      showLoading('Claiming...')
      const tx = await coreW.claimRewards()
      await tx.wait()
      showSuccess('Claim complete.')
      refreshInBackground()
    } catch (e) { console.error(e); showError(extractErr(e)) }
  }

  async function ensureUsdtAllowanceAndPayout() {
    try {
      await initRead()
      const { coreW, usdtW } = await getMM()
      const amtBase = toUnitsSafe(payoutInput.value, usdtDecimals.value)
      if (amtBase <= 0n) return
      const allowance = await usdtRead.value.allowance(account.value, CORE_ADDR.value)
      if (allowance < amtBase) {
        showLoading('Approving USDT for payout...')
        const tx = await usdtW.approve(CORE_ADDR.value, amtBase)
        await tx.wait()
      }
      showLoading('Payout in progress...')
      const tx2 = await coreW.payout(amtBase)
      await tx2.wait()
      showSuccess('Payout complete.')
      refreshInBackground()
    } catch (e) { console.error(e); showError(extractErr(e)) }
  }

  function extractErr(e) {
    try {
      if (e?.info?.error?.message) return e.info.error.message
      if (e?.shortMessage) return e.shortMessage
      if (e?.message) return e.message
    } catch {
      //No Empty
    }
    return 'Transaction reverted'
  }
</script>

<style scoped>
/* Background Images Container */
.bg-images-container {
  width: 100%;
  height: 100%;
}

/* Individual Background Image */
.bg-image {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  opacity: 0;
  animation-duration: 18s;
  animation-timing-function: ease-in-out;
  animation-iteration-count: infinite;
  animation-fill-mode: both;
}

/* Stagger the animations so each image shows for 6 seconds */
.bg-image-1 {
  animation-name: fadeInOut1;
  opacity: 1 !important;
}

.bg-image-2 {
  animation-name: fadeInOut2;
  animation-delay: 6s;
}

.bg-image-3 {
  animation-name: fadeInOut3;
  animation-delay: 12s;
}

/* Keyframes for first image */
@keyframes fadeInOut1 {
  0% { opacity: 1; }
  30% { opacity: 1; }
  33.33% { opacity: 0; }
  100% { opacity: 0; }
}

/* Keyframes for second image */
@keyframes fadeInOut2 {
  0% { opacity: 0; }
  30% { opacity: 0; }
  33.33% { opacity: 1; }
  63.33% { opacity: 1; }
  66.66% { opacity: 0; }
  100% { opacity: 0; }
}

/* Keyframes for third image */
@keyframes fadeInOut3 {
  0% { opacity: 0; }
  63.33% { opacity: 0; }
  66.66% { opacity: 1; }
  96.66% { opacity: 1; }
  100% { opacity: 0; }
}

/* Toast Notification Styles */
.toast-notification {
  position: relative;
  min-width: 280px;
  max-width: 350px;
  animation: slideInRight 0.3s ease-out;
}

.toast-content {
  background: linear-gradient(135deg, rgba(6, 8, 17, 0.95) 0%, rgba(15, 23, 42, 0.9) 100%);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(34, 211, 238, 0.3);
  border-radius: 8px;
  padding: 12px 16px;
  box-shadow: 
    0 4px 20px rgba(34, 211, 238, 0.15),
    0 0 40px rgba(34, 211, 238, 0.05),
    inset 0 1px 0 rgba(255, 255, 255, 0.05);
  position: relative;
  overflow: hidden;
}

.toast-content::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(34, 211, 238, 0.6), transparent);
  animation: scanline 3s linear infinite;
}

.toast-glow {
  position: absolute;
  inset: -2px;
  background: linear-gradient(45deg, transparent, rgba(34, 211, 238, 0.2), transparent);
  border-radius: 8px;
  opacity: 0;
  animation: glowPulse 2s ease-in-out infinite;
}

@keyframes slideInRight {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@keyframes scanline {
  to {
    left: 100%;
  }
}

@keyframes glowPulse {
  0%, 100% {
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
}

/* Toast transition animations */
.toast-enter-active {
  transition: all 0.3s ease-out;
}

.toast-leave-active {
  transition: all 0.3s ease-in;
}

.toast-enter-from {
  transform: translateX(100%);
  opacity: 0;
}

.toast-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

.toast-move {
  transition: transform 0.3s ease;
}
</style>
